// Snapshot-diff based sound event detection.
//
// Mirrors winbolo's per-module sound trigger logic (tank.c, shells.c,
// lgm.c, minesexp.c) translated into a client-side snapshot diff:
//
//   New shell (own)            → shootSelf
//   New shell (other)          → shootNear
//   Shell removed + Forest     → shotTreeNear
//   Shell removed + structure  → shotBuildingNear
//   Shell removed + open tile  → (silent; shell expired naturally)
//   Armor decrease, alive      → hitTankSelf / hitTankNear
//   Player alive→dead + water  → tankSinkNear
//   Player alive→dead + land   → bigExplosionNear
//   Mine added                 → manLayingMineNear
//   Mine removed               → mineExplosionNear
//   Builder not-busy→busy + forest → farmingTreeNear
//   Builder not-busy→busy + other  → manBuildingNear
//   Builder alive→dead         → manDyingNear

import { TILE_SIZE, Terrain } from "@webolo/shared";
import type { MergedWorldSnapshot } from "@webolo/shared";
import type { SoundType } from "./sound.js";

export interface SoundEvent {
  type: SoundType;
  tileX: number;
  tileY: number;
}

// Terrain types that mean "structural impact" for a shell hit.
const STRUCTURE_TERRAIN = new Set<number>([
  Terrain.BoatOrBuilding,
  Terrain.Wall,
  Terrain.HalfBuiltWall,
  Terrain.Pillbox,
  Terrain.Base,
]);

function toTile(px: number): number {
  return Math.floor(px / TILE_SIZE);
}

function terrainAt(
  tileX: number,
  tileY: number,
  terrain: Uint8Array,
  mapWidth: number,
): number {
  const idx = tileY * mapWidth + tileX;
  return idx >= 0 && idx < terrain.length ? terrain[idx] : Terrain.Grass;
}

interface PlayerRecord {
  alive: boolean;
  armor: number;
}

interface BuilderRecord {
  alive: boolean;
  busy: boolean;
  parachuting: boolean;
}

export class SoundEventTracker {
  // Per shell we remember position, owner, AND the terrain that was under
  // its tile last frame. Server-side a shell that impacts Forest flips the
  // tile to Grass in the same tick it removes the shell — by the time the
  // client tries to classify the impact, the terrain it'd look up is the
  // post-destruction Grass tile, so the shotTreeNear sound would never
  // play. Storing the terrain at insert time means impact classification
  // uses the tile as it was when the shell was alive there.
  private prevShells  = new Map<number, { x: number; y: number; ownerId: number; terrain: number }>();
  private prevMines   = new Map<number, { tileX: number; tileY: number }>();
  private prevPlayers = new Map<number, PlayerRecord>();
  private prevBuilders = new Map<number, BuilderRecord>();
  // Per-pillbox destroyed flag, so a standing→dropped transition (shelled or
  // swallowed by the battle-royale fog) plays one boom. Tracked only on
  // snapshots that carry the (delta-encoded) pillbox list.
  private prevPillDropped = new Map<number, boolean>();
  // Reused id sets, cleared and refilled each snapshot to avoid allocating a
  // fresh Set per snapshot.
  private currShellIds = new Set<number>();
  private currMineIds  = new Set<number>();

  ingest(
    prev: MergedWorldSnapshot | null,
    curr: MergedWorldSnapshot,
    selfId: number | null,
    terrain: Uint8Array | null,
    mapWidth: number,
  ): SoundEvent[] {
    const events: SoundEvent[] = [];
    // mines is delta-encoded over the wire but merged back to a complete list
    // in GameClient, so it is always present here; `?? []` satisfies the type.
    const currMines = curr.mines ?? [];

    if (prev) {
      const currShellIds = this.currShellIds;
      currShellIds.clear();
      for (const s of curr.shells) currShellIds.add(s.id);
      const currMineIds = this.currMineIds;
      currMineIds.clear();
      for (const m of currMines) currMineIds.add(m.id);

      // ── Shells ──────────────────────────────────────────────────────────────
      // New shells → shooting sounds.
      for (const s of curr.shells) {
        if (!this.prevShells.has(s.id)) {
          const tileX = toTile(s.x);
          const tileY = toTile(s.y);
          if (s.ownerId === selfId) {
            events.push({ type: "shootSelf", tileX, tileY });
          } else {
            events.push({ type: "shootNear", tileX, tileY });
          }
        }
      }

      // Removed shells → impact sounds (terrain-based classification).
      // Use the terrain we recorded when the shell was inserted, NOT the
      // current-frame terrain — a forest-destroying impact has already
      // flipped the tile to Grass by the time we classify here.
      for (const [id, ps] of this.prevShells) {
        if (!currShellIds.has(id)) {
          const tileX = toTile(ps.x);
          const tileY = toTile(ps.y);
          if (ps.terrain === Terrain.Forest) {
            events.push({ type: "shotTreeNear", tileX, tileY });
          } else if (STRUCTURE_TERRAIN.has(ps.terrain)) {
            events.push({ type: "shotBuildingNear", tileX, tileY });
          }
          // Open terrain (grass, road, river) → shell expired silently.
        }
      }

      // ── Players ──────────────────────────────────────────────────────────────
      for (const cp of curr.players) {
        const pp = this.prevPlayers.get(cp.id);
        if (!pp) continue;

        // Armor decrease on a living tank → hit sound.
        if (pp.alive && cp.alive && cp.armor < pp.armor) {
          const tileX = toTile(cp.x);
          const tileY = toTile(cp.y);
          if (cp.id === selfId) {
            events.push({ type: "hitTankSelf", tileX, tileY });
          } else {
            events.push({ type: "hitTankNear", tileX, tileY });
          }
        }

        // Alive → dead transition → explosion or sinking.
        if (pp.alive && !cp.alive) {
          const tileX = toTile(cp.x);
          const tileY = toTile(cp.y);
          const t = terrain ? terrainAt(tileX, tileY, terrain, mapWidth) : Terrain.Grass;
          if (t === Terrain.DeepSea) {
            events.push({ type: "tankSinkNear", tileX, tileY });
          } else {
            events.push({ type: "bigExplosionNear", tileX, tileY });
          }
        }
      }

      // ── Mines ───────────────────────────────────────────────────────────────
      // New mine → laying sound.
      for (const m of currMines) {
        if (!this.prevMines.has(m.id)) {
          events.push({ type: "manLayingMineNear", tileX: m.tileX, tileY: m.tileY });
        }
      }

      // Removed mine → explosion sound.
      for (const [id, pm] of this.prevMines) {
        if (!currMineIds.has(id)) {
          events.push({ type: "mineExplosionNear", tileX: pm.tileX, tileY: pm.tileY });
        }
      }

      // ── Pillboxes ─────────────────────────────────────────────────────────
      // A pillbox falling to the ground (shelled down, or eaten by the fog)
      // plays the big-explosion boom. Only diff on snapshots that carry the
      // delta-encoded pillbox list.
      if (curr.pillboxes) {
        for (const pb of curr.pillboxes) {
          if (this.prevPillDropped.get(pb.id) === false && pb.dropped) {
            events.push({ type: "bigExplosionNear", tileX: pb.tileX, tileY: pb.tileY });
          }
        }
      }

      // ── Builders ────────────────────────────────────────────────────────────
      for (const cb of curr.builders) {
        const pb = this.prevBuilders.get(cb.playerId);
        if (!pb) continue;

        const cbParachuting = cb.parachuting === true;

        // Builder died. With the helicopter-respawn parity flow the
        // builder never leaves the snapshot — instead `parachuting`
        // flips from false to true. We trigger the dying sound on
        // either transition: legacy alive→dead (siege/odd cases) AND
        // the new not-parachuting → parachuting transition.
        const died = (pb.alive && !cb.alive)
                  || (!pb.parachuting && cbParachuting);
        if (died) {
          const tileX = toTile(cb.x);
          const tileY = toTile(cb.y);
          events.push({ type: "manDyingNear", tileX, tileY });
        }

        // Builder started working (not-busy → busy).
        if (!pb.busy && cb.busy && cb.alive) {
          const tileX = toTile(cb.x);
          const tileY = toTile(cb.y);
          const t = terrain ? terrainAt(tileX, tileY, terrain, mapWidth) : Terrain.Grass;
          if (t === Terrain.Forest) {
            events.push({ type: "farmingTreeNear", tileX, tileY });
          } else {
            events.push({ type: "manBuildingNear", tileX, tileY });
          }
        }
      }
    }

    // ── Update tracking state ────────────────────────────────────────────────
    this.prevShells.clear();
    for (const s of curr.shells) {
      const tileX = toTile(s.x);
      const tileY = toTile(s.y);
      const t = terrain ? terrainAt(tileX, tileY, terrain, mapWidth) : Terrain.Grass;
      this.prevShells.set(s.id, { x: s.x, y: s.y, ownerId: s.ownerId, terrain: t });
    }

    this.prevMines.clear();
    for (const m of currMines) {
      this.prevMines.set(m.id, { tileX: m.tileX, tileY: m.tileY });
    }

    this.prevPlayers.clear();
    for (const p of curr.players) {
      this.prevPlayers.set(p.id, { alive: p.alive, armor: p.armor });
    }

    this.prevBuilders.clear();
    for (const b of curr.builders) {
      this.prevBuilders.set(b.playerId, {
        alive: b.alive,
        busy: b.busy,
        parachuting: b.parachuting === true,
      });
    }

    // Only refresh the pillbox tracking when this snapshot carried the list;
    // otherwise keep the prior state so a later destruction still diffs cleanly.
    if (curr.pillboxes) {
      this.prevPillDropped.clear();
      for (const pb of curr.pillboxes) this.prevPillDropped.set(pb.id, pb.dropped);
    }

    return events;
  }

  reset(): void {
    this.prevShells.clear();
    this.prevMines.clear();
    this.prevPlayers.clear();
    this.prevBuilders.clear();
    this.prevPillDropped.clear();
  }
}
